import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pill, Heart, ShieldCheck, Warning, Users, FirstAidKit } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const PnPToolkitPage = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState(null);

  const sections = [
    {
      id: 'what-is-pnp',
      emoji: '🎉',
      title: 'What is PnP?',
      subtitle: 'Understanding Party & Play',
      icon: Heart,
      color: 'from-purple-500 to-pink-500',
      content: {
        intro: "Party n' Play (also called chemsex) is when people use drugs to enhance sex. It's common in gay, bi, trans, and queer communities. PnP can be fun for some, but it can also come with risks.",
        sections: [
          {
            title: 'Why People PnP',
            items: [
              'Pleasure and better sex',
              'Feeling less anxious or more confident',
              'Connection, intimacy, and community',
              'Escape or numbing pain',
              'Staying awake longer'
            ]
          },
          {
            title: 'Common Drugs Used',
            items: [
              'Crystal Meth (Tina, T)',
              'GHB / GBL (G, Gina)',
              'Cocaine',
              'Ketamine',
              'MDMA (Molly, Ecstasy)',
              'Poppers'
            ]
          },
          {
            title: 'Key Risks',
            items: [
              'STBBIs (HIV, Hep C, etc.)',
              'Overdose or poisoning (toxic drug supply)',
              'Mental health issues (anxiety, paranoia, depression)',
              'Physical harm (long sessions, lack of sleep, injuries)',
              'Trouble having sex without drugs over time',
              'Stigma and judgment when asking for help'
            ]
          }
        ],
        footer: "Not everyone who does PnP has a problem. Information, safety, respect, and choice save lives."
      }
    },
    {
      id: 'substances',
      emoji: '💊',
      title: 'Substance Guide',
      subtitle: 'Know what you\'re taking',
      icon: Pill,
      color: 'from-cyan-500 to-blue-500',
      content: {
        intro: "Different substances have different effects and risks. Know what you're taking and how to stay safer.",
        substances: [
          {
            name: 'Crystal Meth (Tina)',
            likes: ['Energy', 'Confidence', 'Sex feels intense', 'Staying awake longer'],
            hard: ['Anxiety, paranoia', 'Crash after', 'Sleep loss', 'Dehydration', 'Mental health impact', 'Risk of dependency'],
            tips: ['Drink water (small sips often)', 'Eat before & after', 'Take breaks', 'Don\'t mix with too many substances', 'Sleep after the party', 'Be gentle with yourself after']
          },
          {
            name: 'GHB / GBL (G)',
            likes: ['Relaxation', 'Confidence', 'Sex feels smoother', 'Less anxiety (at first)'],
            hard: ['Easy to overdose', 'Memory loss', 'Blackouts', 'Breathing slows', 'Dangerous with alcohol'],
            tips: ['Measure doses carefully', 'Use a timer (never redose early)', 'Avoid mixing with alcohol or benzos', 'Don\'t use alone', 'If someone won\'t wake up, get help']
          },
          {
            name: 'Ketamine',
            likes: ['Dissociation', 'Calm or floaty feeling', 'Less emotional pain', 'Sex feels different'],
            hard: ['Loss of balance', 'Confusion', 'Bladder issues (with frequent use)', 'Nausea', 'Accidents or falls'],
            tips: ['Sit or lie down', 'Go slow with dosing', 'Drink water', 'Take breaks', 'Avoid mixing with alcohol']
          },
          {
            name: 'MDMA (Molly)',
            likes: ['Love, connection', 'Empathy', 'Touch feels amazing', 'Emotional openness'],
            hard: ['Dehydration or overheating', 'Jaw clenching', 'Low mood after', 'Sleep issues', 'Serotonin crash'],
            tips: ['Drink water', 'Take breaks from dancing/sex', 'Avoid mixing with antidepressants', 'Eat before & after', 'Rest for a few days after']
          },
          {
            name: 'Cocaine',
            likes: ['Energy', 'Confidence', 'Focus', 'Feeling "up"'],
            hard: ['Anxiety, paranoia', 'Heart strain', 'Nose damage', 'Strong crash', 'Mixing risks'],
            tips: ['Take breaks', 'Avoid frequent redosing', 'Rinse nose after', 'Don\'t mix with alcohol (creates toxic combo)', 'Watch your heart & anxiety']
          },
          {
            name: 'Poppers',
            likes: ['Muscle relaxation', 'Stronger orgasms', 'Short, intense rush'],
            hard: ['Headaches', 'Dizziness', 'Blood pressure drop', 'Dangerous with ED meds'],
            tips: ['Take small inhales', 'Sit or lie down', 'Avoid frequent use', 'NEVER mix with ED pills', 'Stop if you feel faint']
          }
        ]
      }
    },
    {
      id: 'safety-tips',
      emoji: '🛡️',
      title: 'Harm Reduction Tips',
      subtitle: 'Party smart, stay safe',
      icon: ShieldCheck,
      color: 'from-green-500 to-emerald-500',
      content: {
        intro: "Party Smart means knowing your body, knowing your limits, caring for yourself, and caring for others. No shame. No rules. Just information.",
        sections: [
          {
            title: 'Before Playing',
            items: [
              'Set boundaries: what sex, what drugs, how long, consent',
              'Eat, hydrate, rest',
              'Plan testing (STBBI every 3-6 months)',
              'Bring your own supplies',
              'Try to test your supplies if possible'
            ]
          },
          {
            title: 'Sexual Health',
            items: [
              'Condoms, lube, dental dams',
              'PrEP if HIV-negative',
              'PEP if recent HIV exposure (within 72 hours)',
              'TasP = undetectable = untransmittable (U=U)',
              'Hep A & B vaccines'
            ]
          },
          {
            title: 'Drug Safety',
            items: [
              'Start low, go slow',
              'Avoid mixing drugs when possible',
              'Don\'t use alone',
              'Carry Naloxone',
              'Use sterile supplies every time',
              'Drug checking if available'
            ]
          },
          {
            title: 'During & After',
            items: [
              'Take breaks',
              'Sleep when you can',
              'Rehydrate and eat',
              'Check in with your body and mental health'
            ]
          }
        ],
        warning: "MIXING DRUGS = HIGHER RISK. Alcohol + G = dangerous. Poppers + ED pills = dangerous. Use less when mixing. Space doses. If unsure, don't mix."
      }
    },
    {
      id: 'hiv-partying',
      emoji: '💙',
      title: 'HIV & Partying',
      subtitle: 'Treatment, meds & staying safe',
      icon: FirstAidKit,
      color: 'from-blue-500 to-indigo-500',
      content: {
        intro: "Living with HIV does not stop you from living your life. But partying can affect treatment if you're not careful. Party smart = knowing your status & options.",
        sections: [
          {
            title: 'Party Smart Rule',
            text: 'When you are on HIV treatment, your body may react stronger to drugs and alcohol. You might need less to feel the same effect.',
            highlight: 'Start low. Go slow.'
          },
          {
            title: 'Important Drug Interactions',
            items: [
              'MDMA & Crystal Meth + some HIV meds (PIs): Can build up to dangerous levels, may cause long-term depression',
              'ED pills + HIV meds (PIs): Can strain the heart. Adding poppers = higher risk. Never mix ED pills with poppers.',
              'Crystal Meth: Can affect remembering meds. Lack of sleep + toxins can raise viral load',
              'Alcohol (heavy use): Hard on liver & pancreas. HIV meds also stress the liver'
            ]
          },
          {
            title: 'How Partying Affects Treatment',
            items: [
              'Missing doses: Long nights, sleeping all day, forgetting when high. Missing doses can make treatment less effective.',
              'Mixing meds & substances: Some drugs stress liver and kidneys. Can increase side effects.',
              'Mental health: Partying can increase anxiety, depression, paranoia. Crashes can affect motivation to take meds.'
            ]
          },
          {
            title: 'U=U & Partying',
            highlight: "When your viral load is undetectable, HIV cannot be passed on sexually. U=U still works when you party - as long as you take your meds regularly.",
            items: [
              'You can party and stay undetectable',
              'You don\'t need to be perfect',
              'Small steps still matter',
              'Treatment = protection for you and others'
            ]
          }
        ],
        tips: [
          'Take meds before the party if possible',
          'Set a phone reminder',
          'Keep meds somewhere easy to reach',
          'Eat before taking meds',
          'Be gentle with yourself after'
        ]
      }
    },
    {
      id: 'consent',
      emoji: '🤝',
      title: 'Consent & Community',
      subtitle: 'Care for yourself & others',
      icon: Users,
      color: 'from-pink-500 to-rose-500',
      content: {
        intro: "Consent matters more when partying. Partying changes judgment. Care for others = care for yourself.",
        sections: [
          {
            title: 'Party Smart Looks Like',
            items: [
              'Checking in: "Are you okay?"',
              'Checking in with friends',
              'Helping someone who looks unwell',
              'Not leaving them alone',
              'Carrying naloxone kit',
              'Calling for help when needed'
            ]
          },
          {
            title: 'Before',
            items: [
              'Eat a good meal',
              'Hydrate',
              'Bring supplies',
              'Set intentions and limits'
            ]
          },
          {
            title: 'During',
            items: [
              'Drink water',
              'Check your body',
              'Take breaks',
              'Watch your friends'
            ]
          },
          {
            title: 'After',
            items: [
              'Sleep',
              'Shower',
              'Brush teeth',
              'Warm food',
              'Gentle care',
              'No shame about the crash'
            ]
          }
        ],
        footer: "Never don't care. Respect your body and buddies' boundaries."
      }
    },
    {
      id: 'when-to-help',
      emoji: '🚨',
      title: 'When to Get Help',
      subtitle: 'Emergency signs to watch for',
      icon: Warning,
      color: 'from-red-500 to-orange-500',
      content: {
        intro: "Sometimes things go wrong. Knowing when to get help can save a life.",
        sections: [
          {
            title: 'Get Help If Someone Has',
            items: [
              'Trouble breathing',
              'Blue lips',
              'Unconsciousness',
              'Seizures',
              'Extreme confusion',
              'Won\'t wake up'
            ]
          },
          {
            title: 'What To Do',
            items: [
              'Call 911 - calling for help saves lives',
              'Use naloxone if available',
              'Put them in recovery position (on their side)',
              'Stay with them until help arrives',
              'Tell paramedics what they took if you know'
            ]
          }
        ],
        footer: "You are not broken. You are not weak. You deserve pleasure and safety. Party Smart, Play Safe. Care for yourself, care for others."
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedSection ? setSelectedSection(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🎯</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedSection ? sections.find(s => s.id === selectedSection)?.title : 'PnP Toolkit'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedSection ? (
          <>
            {/* Hero */}
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">Party Smart Toolkit 2026</h2>
              <p className="text-white/90 leading-relaxed">
                You don't need to quit to care. You deserve pleasure and safety. No shame. No rules. Just information. 💜
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    data-testid={`section-${section.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${section.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-5xl mr-4 emoji-bounce">{section.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {section.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {section.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Big takeaway */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
              <p className="text-[rgb(var(--text-primary))] font-bold mb-2">
                Big Takeaway
              </p>
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                PnP is not one thing. Some people manage it well. Some need support. Information, safety, respect, and choice save lives.
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {sections.find(s => s.id === selectedSection)?.content && (() => {
              const section = sections.find(s => s.id === selectedSection);
              const content = section.content;
              
              return (
                <>
                  {/* Intro */}
                  <div className={`p-6 bg-gradient-to-br ${section.color} rounded-3xl text-white`}>
                    <p className="leading-relaxed">{content.intro}</p>
                  </div>

                  {/* Substances (special layout) */}
                  {content.substances && content.substances.map((sub, idx) => (
                    <div key={idx} className="bg-[rgb(var(--surface))] rounded-2xl p-5 border border-white/5">
                      <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-4">{sub.name}</h3>
                      
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-[rgb(var(--success))] uppercase mb-1">What people like</p>
                          <p className="text-sm text-[rgb(var(--text-secondary))]">{sub.likes.join(' • ')}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[rgb(var(--danger))] uppercase mb-1">What can be hard</p>
                          <p className="text-sm text-[rgb(var(--text-secondary))]">{sub.hard.join(' • ')}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[rgb(var(--primary))] uppercase mb-1">Party Smart tips</p>
                          <ul className="space-y-1">
                            {sub.tips.map((tip, i) => (
                              <li key={i} className="text-sm text-[rgb(var(--text-secondary))] flex items-start">
                                <span className="text-[rgb(var(--primary))] mr-2">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Regular sections */}
                  {content.sections && content.sections.map((sect, idx) => (
                    <div key={idx} className="bg-[rgb(var(--surface))] rounded-2xl p-5 border border-white/5">
                      <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">{sect.title}</h3>
                      {sect.text && <p className="text-sm text-[rgb(var(--text-secondary))] mb-3">{sect.text}</p>}
                      {sect.highlight && (
                        <p className="text-[rgb(var(--primary))] font-bold mb-3">{sect.highlight}</p>
                      )}
                      {sect.items && (
                        <ul className="space-y-2">
                          {sect.items.map((item, i) => (
                            <li key={i} className="text-sm text-[rgb(var(--text-secondary))] flex items-start">
                              <span className="text-[rgb(var(--primary))] mr-2">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {/* Tips */}
                  {content.tips && (
                    <div className="bg-[rgb(var(--surface))] rounded-2xl p-5 border border-white/5">
                      <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-3">Party Smart Tips</h3>
                      <ul className="space-y-2">
                        {content.tips.map((tip, i) => (
                          <li key={i} className="text-sm text-[rgb(var(--text-secondary))] flex items-start">
                            <span className="text-[rgb(var(--success))] mr-2">✓</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warning */}
                  {content.warning && (
                    <div className="p-4 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/30 rounded-2xl">
                      <p className="text-sm text-[rgb(var(--text-secondary))]">
                        <strong className="text-[rgb(var(--danger))]">Warning:</strong> {content.warning}
                      </p>
                    </div>
                  )}

                  {/* Footer */}
                  {content.footer && (
                    <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
                      <p className="text-[rgb(var(--text-primary))] font-semibold">{content.footer}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default PnPToolkitPage;
